import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { BootstrapData } from "@czasomat/contracts";

export function hasOperationPermission(data: BootstrapData, permission: string): boolean {
  return data.auth.permissions.includes("all") || data.auth.permissions.includes(permission);
}

export function operationEmployees(data: BootstrapData, foreignPermission: string) {
  return hasOperationPermission(data, foreignPermission) ? data.employees
    : data.employees.filter((employee) => Boolean(data.auth.employeeId) && employee.id === data.auth.employeeId);
}

export function canSelectOperationEmployee(data: BootstrapData, employeeId: unknown, foreignPermission: string): boolean {
  return typeof employeeId === "string" && operationEmployees(data, foreignPermission).some((employee) => employee.id === employeeId);
}

export function canLogOperationTask(data: BootstrapData, task: Record<string, any>): boolean {
  return hasOperationPermission(data, "tasks.log") && (hasOperationPermission(data, "tasks.write")
    || Boolean(data.auth.employeeId && task.employeeId === data.auth.employeeId));
}

export function visibleOperationNotification(data: BootstrapData, item: Record<string, any>): boolean {
  if (item.kind === "request.workflow") return item.userId === data.auth.id && ["self.read", "requests.create", "requests.decide"].some((permission) => hasOperationPermission(data, permission));
  if (hasOperationPermission(data, "all")) return true;
  if (!hasOperationPermission(data, "self.read")) return false;
  if (item.userId && item.userId !== data.auth.id) return false;
  if (item.employeeId && item.employeeId !== data.auth.employeeId) return false;
  return item.audience === "all" || item.audience === "employee"
    || (item.audience === "manager" && hasOperationPermission(data, "employees.read"));
}

export function useModuleOperation<T>({ allowed, send, close, message, setMessage }: {
  allowed: (variables: T) => boolean;
  send: (variables: T) => Promise<unknown>;
  close?: () => void;
  message: string | ((variables: T) => string);
  setMessage: (message: string) => void;
}) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (variables: T) => {
      setMessage("");
      if (!allowed(variables)) throw new Error("Brak uprawnień lub dostępnego pracownika do tej operacji.");
      return send(variables);
    },
    onSuccess: async (_result, variables) => {
      close?.();
      await client.invalidateQueries({ queryKey: ["bootstrap"] });
      setMessage(typeof message === "function" ? message(variables) : message);
    }
  });
}

export function OperationError({ error }: { error: unknown }) {
  return error ? <p role="alert" className="form-error field--full">{error instanceof Error ? error.message : "Nie udało się wykonać operacji."}</p> : null;
}

export function OperationEmployeeField({ data, foreignPermission }: { data: BootstrapData; foreignPermission: string }) {
  const employees = operationEmployees(data, foreignPermission);
  const selected = employees.some((employee) => employee.id === data.auth.employeeId) ? data.auth.employeeId! : "";
  return <select name="employeeId" required defaultValue={selected}><option value="" disabled>Wybierz pracownika</option>{employees.map((employee) => <option key={employee.id} value={employee.id}>{employee.firstName} {employee.lastName}</option>)}</select>;
}
